import { useCallback, ReactNode } from 'react';
import { GlobalPullToRefresh } from './PullToRefresh';

/**
 * Wraps the authenticated app in the global pull-to-refresh gesture.
 *
 * The refresh clears every service-worker cache before reloading, because the
 * gesture's job on mobile is "give me the current version" — a reload that
 * serves the same cached bundle looks broken to the person who pulled.
 */
export default function PullToRefreshShell({ children }: { children: ReactNode }) {
  const handleGlobalRefresh = useCallback(async (): Promise<void> => {
    // Hold the "Refreshing..." indicator on screen for at least 400ms so iOS
    // users get visible feedback before the webview is torn down by the reload
    // — otherwise the spinner disappears too fast to notice on a fast
    // connection, and the gesture reads as having done nothing.
    const minVisible = new Promise(resolve => setTimeout(resolve, 400));

    const clearAllCaches = async (): Promise<void> => {
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map(key => caches.delete(key)));
        }
      } catch (err: unknown) {
        // A refusal to clear caches must not block the reload — the user still
        // gets a fresher page than they had.
        console.warn('[PullToRefresh] cache clear failed:', err);
      }
    };

    await Promise.all([clearAllCaches(), minVisible]);
    window.location.reload();
  }, []);

  return <GlobalPullToRefresh onRefresh={handleGlobalRefresh}>{children}</GlobalPullToRefresh>;
}
